import { PinIcon, PinOffIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { FaGlasses, FaHistory, FaUserCircle } from "react-icons/fa";
import {
  HiCalendar,
  HiPencil,
  HiTrash
} from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import store from "../../../redux/store";
import { useNavigate } from "react-router-dom";
import DashboardStatusBadge from "../../../components/common/DashboardStatusBadge";
import RiskStatusDropdown from "../../../components/common/RiskStatusDropdown";
import TruncatedText from "../../../components/common/TruncatedText";
import Cost from "../../../components/Cost";
import ProgressCircle from "../../../components/features/ProgressCircle/ProgressCircle";
import { defaultUserNames } from "../../../constants/defaultUsers";
import {
  dashboardStatusHexColors,
  getDashboardStatusColor,
  getDashboardStatusLabel,
} from "../../../constants/integrationTypes";
import { getRiskDetailsFromStats } from "../../../helpers/riskStatusHelper";
import { useResourceInfo } from "../../../hooks/useResourceInfo";
import { EntityCountDisplay } from "../../../pages/Projects/sections/Project/sections/EntityCountDisplay";
import WorkItemOptions from "../../../pages/Projects/sections/Project/sections/WorkItemOptions";
import { clearWorkItemsData } from "../../../redux/slices/organization";
import { resetRoadmap, setSingleRoadmap } from "../../../redux/slices/roadmap";
import { resetTimelineState } from "../../../redux/slices/timeline";
import { ReduxStateType } from "../../../redux/store";
import { useOrganizationService } from "../../../services";
import { StrategyEntityItemType } from "../../../services/strategyMap";
import TooltipWrapper from "../../../shared/atoms/TooltipWrapper";
import {
  getChildEntityBreakdown,
  getChildItemCount,
  getEntityTypeDisplay,
} from "../../../utils/entityDisplayUtils";
import { toRelativeTime } from "../../../utils/timeUtils";
import { WorkUnitResourceMembers } from "./WorkUnitResourceMember/WorkUnitResourceMembers";

interface StrategyMapThumbnailProps {
  data: StrategyEntityItemType;
  onEdit: (data: any) => void;
  onDelete?: (data: any) => void;
  minimzed?: boolean;
  onClick?: (data: any) => void;
}

const WorkUnitThumbnail: React.FC<StrategyMapThumbnailProps> = ({
  data,
  onEdit,
  minimzed = false,
  onDelete,
  onClick,
}) => {

  const [isPinned, setIsPinned] = useState(data.pinned);
  const pinnedProjectsIds = useSelector(
    (state: ReduxStateType) => state.organization.pinnedProjectsIds,
  );
  const { getResourceName } = useResourceInfo();
  const { pinWorkItem, fetchRoadmapsAndProjects } = useOrganizationService();

  const getDisplayCreatorName = (id: string | undefined) => {
    if (!id) return "";
    if (parseInt(id) <= 0) {
      return defaultUserNames[id] || "System";
    }
    return getResourceName(id);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const progress = data.progress || 0;
  const startDate = data.start_date || "";
  const endDate = data.end_date || "";

  const getTimeline = () => {
    if (!startDate || !endDate) return "No date range";
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const day1 = start.getDate();
      const month1 = start.toLocaleString("en-US", { month: "short" });
      const year1 = start.getFullYear().toString().slice(-2);
      const day2 = end.getDate();
      const month2 = end.toLocaleString("en-US", { month: "short" });
      const year2 = end.getFullYear().toString().slice(-2);
      return `${day1} ${month1}, ${year1} - ${day2} ${month2}, ${year2}`;
    } catch (e) {
      return "Invalid date range";
    }
  };

  const timeline = getTimeline();

  // Determine entity info - hardcoding for Strategy Map as it's specific
  const entityInfo = getEntityTypeDisplay(
    data?.entity_type ?? "strategy_map",
    data.type,
  );

  const IconComponent = entityInfo.icon;

  const countEntities = data?.entities?.length || 0;
  const count = countEntities;

  const statusColor =
    data.status && dashboardStatusHexColors[(data.status || "").toLowerCase()]
      ? dashboardStatusHexColors[(data.status || "").toLowerCase()]
      : "#6366f1";

  const isSimPending = data.is_sim_pending;

  const pinned = data.pinned;

  const isPinnedByApi = pinnedProjectsIds.includes(data.id);

  const handlePinClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const newPinnedStatus = !isPinned;

    // Optimistic update locally
    setIsPinned(newPinnedStatus);

   

    try {
      await pinWorkItem({
        id: data.id,
        type: data.type || data.entity_type || 'project',
        pin: newPinnedStatus
      });

      // Refetch the work items list to reflect pin/unpin changes
      // (especially important when the pinned filter is active)
      const {
        workItemsSearchTerm = "",
        workItemsSearchType = "all",
        workItemsSortBy = "modified_at_desc",
        workItemsSourceFilter = "",
        workItemsQuarterFilter = [],
        workItemsPinnedFilter = false,
      } = store.getState().organization;

      dispatch(clearWorkItemsData());
      fetchRoadmapsAndProjects(
        true,
        undefined,
        workItemsSearchTerm,
        workItemsSearchType,
        workItemsSortBy,
        workItemsSourceFilter,
        workItemsQuarterFilter,
        workItemsPinnedFilter,
      );
    } catch (error) {
      console.error("Failed to pin item:", error);
      // Rollback on error
      const rolledBackStatus = !newPinnedStatus;
      setIsPinned(rolledBackStatus);
     
    }
  };

  useEffect(() => {
          setIsPinned(isPinnedByApi);
        }, [isPinnedByApi]);
  

  return (
    <div
      className="group relative w-full hover-card"
      data-onboarding-card={data.status === "at_risk" ? "at-risk" : "other"}
      data-onboarding-entity-name={data.name}
      data-onboarding-entity-id={data.id}
    >
      <div className="block cursor-pointer">
        {/* FIXED HEIGHT CARD - 170px */}
        <div
          className={`flex flex-col bg-white dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 
            hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-md
            transition-all duration-150 rounded-lg overflow-hidden border-l-[4px] ${minimzed ? "h-[130px]" : "h-[170px]"}`}
          style={{ borderLeftColor: statusColor }}
        >
          {/* Content with reduced top padding */}
          <div
            className="flex-1 pt-1 px-3 pb-3 flex flex-col relative"
            onClick={() => {
              dispatch(resetTimelineState());
              dispatch(resetRoadmap());
              dispatch(setSingleRoadmap({} as any));
              onClick?.(data);
              // navigate(`/strategymaps/${data.id}`)
            }}
          >
            {/* Edit/Delete Actions - Originally WorkItemOptions */}
            {/* Edit/Delete Actions - Originally WorkItemOptions */}
            <div onClick={(e) => e.stopPropagation()}>
              {entityInfo.type === "strategy_map" ? (
                <>
                  <HiPencil
                    className={`group-hover:block hidden text-lg mr-1 absolute cursor-pointer text-green-500 hover:text-green-600 z-20 transition-colors ${
                      isSimPending ? "right-16" : "right-8"
                    } top-2`}
                    onClick={(e) => {
                      e.preventDefault();
                      onEdit(data);
                    }}
                  />
                  <HiTrash
                    className={`group-hover:block hidden text-lg mr-1 absolute cursor-pointer text-red-500 hover:text-red-600 z-20 transition-colors ${
                      isSimPending ? "right-10" : "right-2"
                    } top-2`}
                    onClick={(e) => {
                      e.preventDefault();
                      onDelete?.(data);
                    }}
                  />
                </>
              ) : (
                <div
                  className={` ${isSimPending ? "right-10" : "right-2"} absolute top-0 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 rounded-bl-lg p-1`}
                >
                  <TooltipWrapper message={isPinned ? "Unpin" : "Pin"}>
                    {isPinned ? (
                      <PinOffIcon
                        className={`card-pin-icon`}
                        onClick={(e) => {
                          e.preventDefault();
                          handlePinClick(e);
                        }}
                      ></PinOffIcon>
                    ) : (
                      <PinIcon
                        className={`card-pin-icon`}
                        onClick={(e) => {
                          e.preventDefault();
                          handlePinClick(e);
                        }}
                      />
                    )}
                  </TooltipWrapper>
                  <WorkItemOptions
                    data={data}
                    className="!px-0"
                  ></WorkItemOptions>
                </div>
              )}
            </div>

            {/* Sim Pending Flag */}
            {isSimPending && (
              <div className="absolute top-0 right-2 flex gap-0.5 pointer-events-none">
                <div className="w-7 h-9 bg-[#467966] rounded-bl-2xl rounded-br-2xl flex items-center justify-center pt-1">
                  <FaGlasses className="text-white w-3 h-3" />
                </div>
              </div>
            )}

            {/* ROW 1: Header -> Icon + Type + Stage */}
            <div className="unit-header flex items-center py-2">
              {/* Entity Icon */}
              <div
                className={`rounded-lg flex items-center justify-center flex-shrink-0`}
              >
                <div className="flex-shrink-0 flex items-center">
                  <ProgressCircle
                    progress={data.progress || 0}
                    size={40}
                    strokeWidth={4}
                    color={statusColor}
                    showPercentage={true}
                  />
                </div>
              </div>

              {/* Unit Header Content */}

              <div className="unit-header-content flex flex-col py-2 ml-2 w-full">
                <div className="unit-title overflow-hidden">
                  <TruncatedText
                    text={data.name || "Untitled Strategy Map"}
                    className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate max-w-[70%]"
                    as="h3"
                    alwaysShowTooltip={false}
                  />
                </div>

                {/* Type + Count + Stage */}
                <div className="flex ">
                  <div className=" min-w-0 flex items-center overflow-hidden gap-1">
                    <IconComponent
                      className={`w-[12px] h-[12px] ${entityInfo.iconClass}`}
                    />
                    <span
                      className={`text-[10px] font-semibold uppercase ${entityInfo.labelClass} `}
                    >
                      {entityInfo.label}
                      {data.integration_data?.integration_type
                        ? ` (${data.integration_data?.integration_type})`
                        : ""}

                      {data.stage ? ` - ${data.stage}` : ""}
                    </span>
                  </div>

                  {getChildItemCount(data) > 0 && (
                    <EntityCountDisplay
                      count={getChildItemCount(data)}
                      childEntityTypes={data.child_entity_types}
                      entityType={data.entity_type}
                      className="text-[10px]"
                      showSeparator={true}
                      textSize="10px"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              {/* ROW 2: Status Badge + Progress */}
              <div className=" flex items-center gap-2 mb-1">
                {/* Status Badge */}

                <div className="flex items-center h-full gap-2">
                  {data.status === "at_risk" ? (
                    <RiskStatusDropdown
                      entityName={data.name || "Untitled Project"}
                      entityType={entityInfo.aiEntityType as any}
                      entityId={data.id}
                      riskDetails={getRiskDetailsFromStats(
                        data.stats,
                        getChildEntityBreakdown(data.child_entity_types)[0]
                          ?.type || "work item",
                      )}
                      status={data.status}
                    >
                      <DashboardStatusBadge
                        status={data.status ?? undefined}
                        withRiskHighlight={true}
                        badgeClassName="text-[10px] px-2 py-1"
                      />
                    </RiskStatusDropdown>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2 py-1.5 rounded-md text-white text-[10px] font-medium ${getDashboardStatusColor(data.status || "")}`}
                    >
                      {getDashboardStatusLabel(data.status || "")}
                    </span>
                  )}
                </div>

                <Cost project_costs={data.costs} />

                {minimzed ? (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="flex items-center gap-1 text-[10px] rounded-full px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">
                      <FaHistory className="w-3 h-3" />
                      <span className="text-[9px]">
                        {toRelativeTime(data.modified_at)}
                      </span>
                    </span>
                  </div>
                ) : (
                  <div className="flex ml-auto">
                    {data.members && data.members.length > 0 && (
                      <div className="flex-shrink-0">
                        <WorkUnitResourceMembers
                          resources={data.members}
                          projectId={data.id}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Progress Bar */}
                {/* <AnimatedProgressBar progress={progress || 0} /> */}
              </div>
              {minimzed && (
                <TooltipWrapper
                  message={`Created by ${data?.created_by ? getDisplayCreatorName(data.created_by) : "Cadence Pro"} on ${new Date(
                    data?.created_at || "",
                  ).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}`}
                  placement="bottom-start"
                >
                  <div className="flex gap-0.5  text-[8px] text-gray-500 dark:text-slate-400 justify-between pt-1">
                    <div className="flex items-center gap-1">
                      <HiCalendar className="w-3 h-3" />
                      <span>{timeline}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <div className="flex items-center truncate gap-1">
                        <span className="truncate font-medium">
                          {data?.created_by
                            ? getDisplayCreatorName(data.created_by)
                            : "Cadence"}
                          {" - "}
                          <span>
                            {new Date(
                              data?.created_at || "",
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </TooltipWrapper>
              )}
            </div>

            {!minimzed && (
              <div className="flex items-end justify-between text-[9px] text-gray-500 dark:text-slate-400 mt-auto pt-1 border-t border-gray-100 dark:border-slate-700/50">
                {/* Left: Timeline + Modified */}

                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1">
                    <HiCalendar className="w-3 h-3" />
                    <span>{timeline}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <TooltipWrapper
                      message={`Created by ${data?.created_by ? getDisplayCreatorName(data.created_by) : "Cadence Pro"} on ${new Date(
                        data?.created_at || "",
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`}
                      placement="bottom-start"
                    >
                      <div className="flex items-center truncate gap-1">
                        <FaUserCircle className="w-3 h-3" />
                        <span className="truncate font-medium">
                          {data?.created_by
                            ? getDisplayCreatorName(data.created_by)
                            : "Cadence Pro"}
                          {" - "}
                          <span>
                            {new Date(
                              data?.created_at || "",
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </span>
                      </div>
                    </TooltipWrapper>
                  </div>
                </div>

                {/* Right: Cost + Members */}

                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-[10px] rounded-full px-2 py-1 bg-gray-100 dark:bg-slate-700">
                    <FaHistory className="w-3 h-3" />
                    <span>{toRelativeTime(data.modified_at)}</span>
                  </span>
                </div>
              </div>
            )}
            {/* ROW 4: Footer */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkUnitThumbnail;
